import { useEffect, useMemo, useRef, useState } from 'react';

import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FiCheckCircle } from 'react-icons/fi';

import Button from '@components/Button';

import FieldRenderer from './FieldRenderer';
import { evaluateConditions, buildValidationSchema } from './utils';
import {
  DynamicFormProps,
  FormField,
  ExtraFormField,
  RenderableField,
} from './types';
import * as S from './styles';

const mergeFieldsWithExtras = (
  formFields: FormField[],
  extraFields: ExtraFormField[] = [],
  formId: number,
): RenderableField[] => {
  if (!extraFields.length) {
    return formFields;
  }

  const sortedFormFields = [...formFields].sort((a, b) => a.order - b.order);
  const result: RenderableField[] = [];
  const insertedExtras = new Set<string>();

  // Helper to recursively insert extras after a field name
  const insertExtrasAfter = (fieldName: string) => {
    const fieldsToInsert = extraFields.filter(
      (extra) =>
        extra.insertAfter === fieldName && !insertedExtras.has(extra.id),
    );

    fieldsToInsert.forEach((extra) => {
      insertedExtras.add(extra.id);
      result.push({ ...extra, form_id: formId });
      // Recursively check if any extras should come after this one
      insertExtrasAfter(extra.name);
    });
  };

  // Process form fields and insert extras after each
  sortedFormFields.forEach((field) => {
    result.push(field);
    insertExtrasAfter(field.name);
  });

  // Add remaining extras without valid insertAfter at the end
  extraFields.forEach((extra) => {
    if (!insertedExtras.has(extra.id)) {
      result.push({ ...extra, form_id: formId });
    }
  });

  return result;
};

const getWatchedFieldNames = (fields: RenderableField[]): string[] => {
  const names = new Set<string>();
  fields.forEach((field) => {
    field.conditions?.forEach((condition) => {
      names.add(condition.field);
    });
    // Watch confirm_field for password confirmation
    if (field.confirm_field) {
      names.add(field.confirm_field);
    }
  });

  return Array.from(names);
};

const getFieldDefaultValues = (
  fields: RenderableField[],
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  fields.forEach((field) => {
    if (field.default_value !== undefined) {
      values[field.name] = field.default_value;
    }
  });

  return values;
};

const createWatchedValuesMap = (
  fieldNames: string[],
  watchedValues: unknown[] | undefined,
): Record<string, unknown> => {
  const map: Record<string, unknown> = {};
  fieldNames.forEach((name, index) => {
    map[name] = watchedValues?.[index];
  });

  return map;
};

const getVisibleFields = (
  fields: RenderableField[],
  watchedValuesMap: Record<string, unknown>,
): Set<string> => {
  const visible = new Set<string>();
  fields.forEach((field) => {
    if (evaluateConditions(field.conditions, watchedValuesMap)) {
      visible.add(field.name);
    }
  });

  return visible;
};

const filterSubmissionData = (
  data: Record<string, unknown>,
  visibleFields: Set<string>,
): Record<string, unknown> => {
  const filteredData: Record<string, unknown> = {};
  Object.keys(data).forEach((key) => {
    if (visibleFields.has(key)) {
      filteredData[key] = data[key];
    }
  });

  // Merge _other fields: se "campo_other" tem valor, usa ele como "campo"
  Object.keys(data).forEach((key) => {
    if (key.endsWith('_other') && data[key]) {
      const originalKey = key.replace(/_other$/, '');
      if (visibleFields.has(originalKey)) {
        filteredData[originalKey] = data[key];
      }
    }
  });

  // Remove campos _other do payload final
  Object.keys(filteredData).forEach((key) => {
    if (key.endsWith('_other')) {
      delete filteredData[key];
    }
  });

  return filteredData;
};

type YupValidationError = {
  inner: Array<{ path: string; message: string }>;
};

const isYupValidationError = (error: unknown): error is YupValidationError => {
  if (error === null || typeof error !== 'object') {
    return false;
  }
  const errorObj = error as Record<string, unknown>;
  return 'inner' in errorObj && Array.isArray(errorObj.inner);
};

const handleYupValidationErrors = (
  error: YupValidationError,
  setError: (name: string, error: { type: string; message: string }) => void,
) => {
  error.inner.forEach((err) => {
    if (err.path) {
      setError(err.path, { type: 'manual', message: err.message });
    }
  });
};

const shouldRenderField = (
  field: RenderableField,
  visibleFields: Set<string>,
): boolean => visibleFields.has(field.name) || field.type === 'hidden';

type SuccessMessageProps = {
  title?: string;
  message: string;
};

const SuccessMessageView = ({ title, message }: SuccessMessageProps) => (
  <S.SuccessMessage>
    <FiCheckCircle size={48} />
    <h3>{title || 'Enviado com sucesso!'}</h3>
    <p>{message}</p>
  </S.SuccessMessage>
);

const DynamicForm = ({
  form,
  onSubmit,
  isLoading = false,
  defaultValues = {},
  extraFields = [],
  submitDisabled = false,
  children,
}: DynamicFormProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const allFields = useMemo(
    () => mergeFieldsWithExtras(form.fields, extraFields, form.id),
    [form.fields, form.id, extraFields],
  );

  const watchedFieldNames = useMemo(
    () => getWatchedFieldNames(allFields),
    [allFields],
  );

  const fieldDefaultValues = useMemo(
    () => getFieldDefaultValues(allFields),
    [allFields],
  );

  const formMethods = useForm({
    defaultValues: {
      ...fieldDefaultValues,
      ...defaultValues,
    },
  });

  const watchedValues = useWatch({
    control: formMethods.control,
    name: watchedFieldNames,
  });

  const watchedValuesMap = useMemo(
    () => createWatchedValuesMap(watchedFieldNames, watchedValues),
    [watchedFieldNames, watchedValues],
  );

  const visibleFields = useMemo(
    () => getVisibleFields(allFields, watchedValuesMap),
    [allFields, watchedValuesMap],
  );

  const validationSchema = useMemo(
    () => buildValidationSchema(allFields, visibleFields),
    [allFields, visibleFields],
  );

  useEffect(() => {
    formMethods.clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationSchema]);

  const handleFormSubmit = async (data: Record<string, unknown>) => {
    const filteredData = filterSubmissionData(data, visibleFields);

    try {
      await validationSchema.validate(filteredData, { abortEarly: false });
      await onSubmit(filteredData);
      if (isMountedRef.current) {
        setIsSubmitted(true);
      }
    } catch (error) {
      if (isYupValidationError(error) && isMountedRef.current) {
        handleYupValidationErrors(error, formMethods.setError);
      }
    }
  };

  if (isSubmitted && form.success_message) {
    return (
      <SuccessMessageView
        title={form.success_title}
        message={form.success_message}
      />
    );
  }

  return (
    <FormProvider {...formMethods}>
      <S.FormContainer onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
        {form.description && (
          <S.ParagraphElement>{form.description}</S.ParagraphElement>
        )}

        <S.FieldsGrid>
          {allFields
            .filter((f) => shouldRenderField(f, visibleFields))
            .map((field) => (
              <FieldRenderer
                key={field.id}
                field={field as FormField}
                disabled={isLoading}
              />
            ))}
        </S.FieldsGrid>

        {children}

        <S.SubmitWrapper>
          <Button
            type="submit"
            title={form.submit_text || 'Enviar'}
            loading={isLoading}
            disabled={submitDisabled}
            pill
          />
        </S.SubmitWrapper>
      </S.FormContainer>
    </FormProvider>
  );
};

export default DynamicForm;
